// src/app/components/reporting/insights-reports-page.tsx
//
// Insights & Reports — one page, three phases.
//
// Source: design_evolution/handoff-reporting.
//
// ── The IA change, and what it collapsed ───────────────────────────────────
//
// The repo shipped three surfaces for what the nav has always presented as one
// destination:
//
//   insights-page.tsx          a standalone saved-insight library
//   report-generator-page.tsx  Generate | History tabs, composer + a 5/7 split
//   report-history-page.tsx    a SECOND history surface, duplicating the tab
//
// The redesign collapses them into Library → Compose → Reports, three phases of
// one page, which is also what the nav claimed they were. The product owner
// signed off on one-page-vs-three-routes before this was built.
//
// The old page ids still resolve: `insights` opens Library and `report-history`
// opens Reports, so a link or a bookmark to either lands where it meant to
// rather than on a dead route. `App.tsx` maps them.
//
// ── The data contract is untouched ─────────────────────────────────────────
//
// `SavedInsight` and `GeneratedReport` keep every field exactly as they were.
// Nothing here adds, renames or removes one. The hooks, the services and the
// endpoints are the ones that were already there.

import { useEffect, useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { ModuleHeader } from "../shell/module-header";
import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { Tabs } from "@/app/components/kit";
import { useInsights } from "../../hooks/reporting/useInsights";
import { useGenerateReport, useReportHistory } from "../../hooks/reporting/useReportGenerator";
import { LibraryPhase } from "./components/library-phase";
import { ComposePhase } from "./components/compose-phase";
import { ReportsPhase } from "./components/reports-phase";
import { ReportDraft } from "./components/report-draft";
import { ReportEditor } from "./components/report-editor";
import { AUDIENCES, FORMATS } from "./types";
import type { GeneratedReport } from "./types";
import "@/styles/reporting-page.css";

export type ReportingPhase = "library" | "compose" | "reports";

interface InsightsReportsPageProps {
  brandName: string;
  brandRunId?: number | null;
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  /** Which phase the route asked for. `insights` → library, `report-history`
      → reports, `report-generator` → library, the design's first phase. */
  initialPhase?: ReportingPhase;
}

export function InsightsReportsPage({
  brandName,
  brandRunId,
  isSidebarCollapsed = false,
  onSidebarToggle,
  initialPhase = "library",
}: InsightsReportsPageProps) {
  const [phase, setPhase] = useState<ReportingPhase>(initialPhase);
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [prompt, setPrompt] = useState("");
  const [audience, setAudience] = useState(AUDIENCES[0]);
  const [format, setFormat] = useState(FORMATS[0]);
  const [draft, setDraft] = useState<GeneratedReport | null>(null);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  // The "Ask" doorway on a library card. `externalQuestion` is the assistant
  // sidebar's own entry point and it fires on a CHANGED timestamp, so asking
  // the same question twice has to carry a new one.
  const [ask, setAsk] = useState<{ text: string; ts: number } | undefined>(undefined);
  const [formatFilter, setFormatFilter] = useState("All");

  const { insights, isLoading: insightsLoading, error: insightsError } = useInsights(brandName);
  const {
    generate,
    isGenerating,
    report: generated,
    error: genError,
  } = useGenerateReport();
  const {
    reports,
    isLoading: reportsLoading,
    error: reportsError,
    refetch: refetchReports,
  } = useReportHistory(brandName);

  // A finished generation lands in Compose with the draft under the studio.
  useEffect(() => {
    if (!generated) return;
    setDraft(generated);
    setEditing(false);
    setPhase("compose");
    refetchReports();
  }, [generated, refetchReports]);

  const selected = useMemo(
    () => insights.filter((i) => selectedIds.includes(i.id)),
    [insights, selectedIds],
  );

  function toggle(id: number) {
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  function handleGenerate() {
    if (!prompt.trim()) return;
    generate({
      brand_name: brandName,
      prompt,
      insight_ids: selectedIds.length > 0 ? selectedIds : undefined,
      brand_run_id: brandRunId,
      audience,
      format,
    });
  }

  /* ── the phase tabs ───────────────────────────────────────────────────────
     A count chip goes on a tab that holds a COLLECTION, and on no other.
     Library counts insights and Reports counts reports. Compose gets none: it
     is a phase, not a collection, and a chip there reads as "4 items in here"
     when it would mean "4 selected". */
  const tabs = [
    {
      value: "library",
      label: "Library",
      badge: insights.length > 0 ? <Count value={insights.length} /> : undefined,
    },
    { value: "compose", label: "Compose" },
    {
      value: "reports",
      label: "Reports",
      badge: reports.length > 0 ? <Count value={reports.length} /> : undefined,
    },
  ];

  // The old page wrote `lg:mr-[380px]`, which is F11's --w-ai-sidebar typed
  // out as an arbitrary value. Same number, from the token.
  const contentMargin = isSidebarCollapsed ? "lg:mr-12" : "lg:mr-(--w-ai-sidebar)";

  return (
    <div className="upm-reporting min-h-screen flex" data-kit>
      <div className={`flex-1 min-w-0 transition-all duration-300 ${contentMargin}`}>
        <ModuleHeader
          page="Insights & Reports"
          icon={FileText}
          title="Assemble intelligence. Generate briefings."
          description="Everything worth remembering from across the platform, composed into something you can send."
        />

        <div className="upm-reporting__tabs">
          <Tabs
            items={tabs}
            value={phase}
            onChange={(v) => setPhase(v as ReportingPhase)}
            label="Insights and reports phases"
          />
        </div>

        <div className="upm-reporting__body">
          {phase === "library" && (
            <LibraryPhase
              insights={insights}
              isLoading={insightsLoading}
              error={insightsError}
              filter={filter}
              onFilterChange={setFilter}
              selectedIds={selectedIds}
              onToggle={toggle}
              onClear={() => setSelectedIds([])}
              onCompose={() => setPhase("compose")}
              onAsk={(insight) =>
                setAsk({
                  // The question is BUILT from the insight rather than typed,
                  // so it cannot describe a reading the card does not show.
                  text: `Why does this matter? "${insight.title}"${
                    insight.metric ? ` (${insight.metric})` : ""
                  }, saved from ${insight.saved_from ?? insight.module}.`,
                  ts: Date.now(),
                })
              }
            />
          )}

          {phase === "compose" && (
            <ComposePhase
              selected={selected}
              prompt={prompt}
              onPromptChange={setPrompt}
              audience={audience}
              onAudienceChange={setAudience}
              format={format}
              onFormatChange={setFormat}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              error={genError}
              onEditSelection={() => setPhase("library")}
              draft={
                draft ? (
                  editing ? (
                    <ReportEditor
                      report={draft}
                      onCancel={() => setEditing(false)}
                      onSaved={(updated) => {
                        setDraft(updated);
                        setEditing(false);
                        refetchReports();
                      }}
                    />
                  ) : (
                    <ReportDraft
                      report={draft}
                      insights={insights}
                      onEdit={() => setEditing(true)}
                      onRefine={() =>
                        setAsk({
                          text: `Help me refine this report: "${draft.title}". It is a ${draft.format} for ${draft.audience}.`,
                          ts: Date.now(),
                        })
                      }
                    />
                  )
                ) : null
              }
            />
          )}

          {phase === "reports" && (
            <ReportsPhase
              reports={reports}
              isLoading={reportsLoading}
              error={reportsError}
              search={search}
              onSearchChange={setSearch}
              formatFilter={formatFilter}
              onFormatFilterChange={setFormatFilter}
              onOpen={(r) => {
                setDraft(r);
                setEditing(false);
                setPhase("compose");
              }}
            />
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="report-generator"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
          externalQuestion={ask}
        />
      </div>
    </div>
  );
}

function Count({ value }: { value: number }) {
  return <span className="upm-reporting__tab-count">{value}</span>;
}
